export interface CreateDiagramInput {
  id: string;
  folderId: string;
  orgId: string;
  ownerUserId?: string | null;
  type: string;
  name: string;
  icon?: string | null; // optional short key
  /** Server-owned, value-free receipt for resumable artifact creation. */
  creationReceipt?: { kind: 'atlas-planning'; sourceDiagramId: string; intentHash: string };
}

export interface CreateTranslatedTemplateInput extends CreateDiagramInput {
  metadata: DiagramMetadata;
  sizeBytes: number;
  snapshotKey?: string;
  storageProvider?: 'r2';
  ydocSnapshot?: string;
  stateVector?: string;
}

export interface CreateTranslatedTemplateResult {
  diagram: Diagram;
  created: boolean;
}

export interface ViewDefinition {
  type: 'people-chart' | 'saved-people-view' | 'planning-chart' | 'group' | 'department' | 'analytics' | 'infra-view' | 'compass-explorer' | 'network-design' | 'position-chart';
  /**
   * For legacy `compass-explorer` / `infra-view` / `group` / `department` /
   * `analytics` views this references the source diagram they were copied
   * from. Canonical folder projections use `sourceFolderId` instead so their
   * facts are hydrated from folder truth rather than a parent Yjs document.
   * For `network-design` it is unused (the design diagram references a
   * parent FOLDER via `baselineFolderId`, not a parent diagram); callers
   * pass an empty string. Optional going forward to make the
   * `network-design` use case ergonomic.
   */
  parentDiagramId?: string;
  groupField?: string;
  groupValue?: string;
  groupLabel?: string;
  includeReportingChain?: boolean;
  presetName?: string;
  presetRules?: Array<{ name: string; display: Record<string, unknown> }>;
  department?: string;
  /** Infrastructure view query that generated this diagram. */
  viewQueryId?: string;
  /** Parameters passed to the query (e.g. vpcId). */
  viewParams?: Record<string, string>;
  /** Graph version at materialization time — for staleness detection. */
  sourceGraphVersion?: number;
  /** Number of resources in this view. */
  resourceCount?: number;
  /**
   * Canonical folder-graph projection rendered by this diagram. The diagram
   * owns presentation and private scenario context; canonical facts remain
   * owned by the folder graph.
   */
  projection?: 'people_chart' | 'planning_chart' | 'position_chart' | 'position_roster';
  /** Stable idempotency key for a generated canonical projection diagram. */
  projectionKey?: string;
  /**
   * Opaque reference to the server-owned definition for a saved Atlas People
   * view. The rules themselves are deliberately never diagram metadata.
   */
  savedViewId?: string;
  /** Canonical folder that owns the projected facts (child view folders differ). */
  sourceFolderId?: string;
  /** Optional effective date for a historical/future projection. */
  asOf?: string | null;
  /** View-only projection filters; canonical facts remain in folder records. */
  filters?: Record<string, unknown>;
  /**
   * Ephemeral, already-authorized membership for a server-owned saved view.
   * Computed at read/export time only — never persisted on the diagram row.
   */
  matchingPersonIds?: readonly string[];
  /**
   * For `type: 'network-design'`: the parent `network` folder whose
   * import state this design diagram was forked from. The design diagram
   * is hydrated client-side from this folder's graph on first open
   * (mirrors the `compass-explorer` lazy-hydration pattern). See
   * docs/architecture/network-design-roundtrip.md.
   */
  baselineFolderId?: string;
  /**
   * For `type: 'network-design'`: ISO timestamp of when the baseline was
   * captured (i.e. when the fork happened). Drives the
   * `DesignFolderBanner` "Baseline imported N days ago — re-fork?"
   * staleness indicator (deferred to DL-5 for the actual re-fork).
   */
  baselineImportedAt?: string;
}

export interface Diagram {
  id: string;
  folderId: string;
  orgId: string;
  ownerUserId?: string | null;
  type: string;
  name: string;
  icon?: string | null;
  isTemplate: boolean;
  ydocSnapshot?: string | null;
  stateVector?: string | null;
  snapshotSeq?: number;
  createdAt: string;
  updatedAt: string;
  // Optional role of the current user (when available from the data source)
  role?: Role;
  // Optional thumbnail URL for legacy diagrams
  thumbnailUrl?: string | null;
  metadata?: DiagramMetadata;
  viewDefinition?: ViewDefinition | null;
  lastHydratedAt?: string | null;
  // R2 storage fields
  storageProvider?: string | null;
  snapshotKey?: string | null;
}

export type DiagramCoverMode = 'auto' | 'area' | 'preset';

export interface DiagramCoverMeta {
  mode: DiagramCoverMode;
  updatedAt: string;
  objectKey?: string | null;
  sourceSnapshotSeq?: number | null;
  selectedShapeIds?: string[];
  frameIds?: string[];
  presetId?: string | null;
}

export interface DiagramMetadata extends Record<string, unknown> {
  cover?: DiagramCoverMeta | null;
  archive?: {
    originFolderId: string;
    archivedAt: string;
  } | null;
  translation?: {
    sourceDiagramId: string;
    sourceRevision: string;
    targetLanguage: string;
    idempotencyKeyHash: string;
    requestFingerprint: string;
  } | null;
}

export interface UpdateSnapshotInput {
  id: string;
  snapshotSeq: number;
  sizeBytes: number;
  updatedAt: Date;
  // Allows replacing live snapshot content even when snapshotSeq does not advance
  // (used by restore flows where update-seq cursor is preserved).
  forceWrite?: boolean;
  // R2 path
  snapshotKey?: string;
  storageProvider?: string;
  // Legacy path (in-memory base64)
  ydocSnapshot?: string;
  stateVector?: string;
}

export interface UpdateThumbnailInput {
  id: string;
  thumbnailUrl: string | null;
  cover?: DiagramCoverMeta | null;
}

export interface CompactionCandidate {
  id: string;
  name: string;
  snapshotSeq: number;
  ydocSnapshot: string | null;
  stateVector: string | null;
  snapshotKey: string | null;
  storageProvider: string | null;
  snapshotMeta?: string | null;
  updatedAt: Date;
  pendingUpdateCount: number;
}

export interface CompactionMeta {
  id: string;
  snapshotSeq: number;
  snapshotMeta: string | null;
}

export interface FindCompactionCandidatesInput {
  minUpdateCount: number;
  cutoffTime: Date;
  limit: number;
}

export interface FindRecentCompactionCandidatesInput {
  modifiedAfter: Date;
  minUpdateCount: number;
  limit: number;
}

export interface ListTemplatesInput {
  searchTerm?: string;
  limit: number;
  offset: number;
}

export interface TemplateSummary {
  id: string;
  name: string;
  thumbnailUrl: string | null;
  updatedAt: string;
}

export interface ListTemplatesResult {
  templates: TemplateSummary[];
  total: number;
}

export interface DiagramVersioningMetadata {
  id: string;
  orgId: string;
  folderId: string;
  ownerUserId: string | null;
  snapshotSeq: number;
  ydocSnapshot: string | null;
  stateVector: string | null;
  snapshotKey: string | null;
  storageProvider: string | null;
  sizeBytes: number | null;
  updatedAt: Date;
}

export interface DiagramRepository {
  create(input: CreateDiagramInput): Promise<Diagram>;
  createTranslatedTemplate?(input: CreateTranslatedTemplateInput): Promise<CreateTranslatedTemplateResult>;
  getById(id: string, region?: string | null, raw?: boolean, includeComments?: boolean): Promise<Diagram | null>;
  getByIds(ids: readonly string[], region?: string | null): Promise<Diagram[]>;
  listByFolder(folderId: string, region?: string | null): Promise<Diagram[]>;
  listOrphans(orgId: string, ownerUserId?: string | null, region?: string | null): Promise<Diagram[]>;
  // Bulk fetch: all diagrams the user can access for an org.
  // For native (Postgres), return all diagrams in the org (ACL TBD).
  // For legacy (Mongo), return diagrams where user is collab for personal or shared via team.
  listAccessible(orgId: string, ownerUserId?: string | null, region?: string | null): Promise<Diagram[]>;
  // Optional bounded variant for startup surfaces that only need recent items.
  listRecentAccessible?(orgId: string, ownerUserId: string | null | undefined, limit: number, region?: string | null): Promise<Diagram[]>;
  // Optional name search pushed down to the store, honouring the same
  // visibility rules as listAccessible. Callers fall back to listAccessible +
  // in-memory filtering for sources that do not implement it.
  searchAccessible?(orgId: string, query: string, ownerUserId?: string | null, limit?: number, region?: string | null): Promise<Diagram[]>;
  listTemplates(input: ListTemplatesInput, region?: string | null): Promise<ListTemplatesResult>;
  deleteById(id: string, region?: string | null): Promise<void>;
  updateName(id: string, name: string, region?: string | null): Promise<Diagram>;
  /** Mark-as-template write path (starter-registry plan PR-I). */
  setIsTemplate(id: string, isTemplate: boolean): Promise<boolean>;
  updateIcon(id: string, icon: string | null, region?: string | null): Promise<Diagram>;
  move(id: string, folderId: string, metadataPatch?: DiagramMetadata): Promise<Diagram>;
  findCompactionCandidates(input: FindCompactionCandidatesInput): Promise<CompactionCandidate[]>;
  findRecentCompactionCandidates(input: FindRecentCompactionCandidatesInput): Promise<CompactionCandidate[]>;
  getCompactionCandidate(diagramId: string): Promise<CompactionCandidate | null>;
  updateSnapshot(input: UpdateSnapshotInput): Promise<boolean>;
  updateThumbnail(input: UpdateThumbnailInput): Promise<Diagram | null>;
  updateSnapshotSequence(id: string, snapshotSeq: number): Promise<void>;
  getVersioningMetadata(diagramId: string): Promise<DiagramVersioningMetadata | null>;
  runInTransaction<T>(fn: () => Promise<T>): Promise<T>;
  copySnapshotFromTemplate(targetDiagramId: string, templateDiagramId: string): Promise<void>;
  updateViewDefinition(id: string, viewDefinition: ViewDefinition | null, lastHydratedAt: Date): Promise<void>;
  getParentCurrentMaxSeq(parentDiagramId: string): Promise<{ snapshotSeq: number; maxUpdateSeq: number } | null>;
  saveRehydratedSnapshot(id: string, ydocSnapshot: string, stateVector: string, sizeBytes: number): Promise<void>;
}

// Role enum colocated per request; native parity planned
export enum Role {
  Owner = 'owner',
  Moderator = 'moderator',
  Editor = 'editor',
  Viewer = 'viewer',
}

export function fromLegacyRole(role?: number): Role | undefined {
  switch (role) {
    case 0: return Role.Owner;
    case 2: return Role.Moderator;
    case 5: return Role.Editor;
    case 10: return Role.Viewer;
    default: return undefined;
  }
}
